import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  BrowserRouter,
  Routes,
  Route,
  useParams,
  useNavigate,
  Navigate
} from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  CommandLineIcon,
  MagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  SunIcon
} from '@heroicons/react/24/outline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { initializeMenuData } from './FeatureData';
import { loadFeatureComponent } from './features';
import { logError } from './utils/error';
import { FeatureItem, MenuGroup } from './types';
import { 
  loadFeatureData, 
  getAccessibleMenuGroups, 
  getUserTenantAndUid, 
  fetchUserCustomClaims 
} from './utils/feature-loader';

// Create context for menu data
const MenuDataContext = React.createContext<MenuGroup[]>([]);
const useMenuData = () => React.useContext(MenuDataContext);

interface ActiveFeature {
  type: 'feature' | 'action';
  id: string;
  label: string;
  group?: string;
}

const logoText = 'Toolbox';

const actions = [
  { id: 'help', label: 'Help', icon: <QuestionMarkCircleIcon className="h-5 w-5" /> },
  { id: 'search', label: 'Search', icon: <MagnifyingGlassIcon className="h-5 w-5" /> },
  { id: 'console', label: 'Console', icon: <CommandLineIcon className="h-5 w-5" /> },
  { id: 'theme', label: 'Toggle theme', icon: <SunIcon className="h-5 w-5" /> },
  { id: 'logout', label: 'Logout', icon: <ArrowRightOnRectangleIcon className="h-5 w-5" /> }
] as const;

type ActionId = typeof actions[number]['id'];

// Route-based feature renderer
const FeatureRoute: React.FC = () => {
  const { featureId } = useParams();
  const menuData = useMenuData();
  const group = menuData.find((g: MenuGroup) => g.features.some((f: FeatureItem) => f.id === featureId));
  const item = group?.features.find((f: FeatureItem) => f.id === featureId);
  if (!item) {
    return <div className="text-sm" style={{ color: 'var(--text-dim)' }}>Feature not found.</div>;
  }
  const Component = loadFeatureComponent(item.id); 
  return (
    <div className="h-full flex flex-col">
      {Component ? (
        <div className="flex-1" style={{ overflow: 'auto', minHeight: 0 }}>
          <React.Suspense fallback={<div className="p-4 text-sm" style={{ color: 'var(--text-dim)' }}>Loading...</div>}>
            <Component />
          </React.Suspense>
        </div>
      ) : (
        <div className="text-sm" style={{ color: 'var(--text-dim)' }}>FeatureRoute-No module implemented yet for id "{item.id}".</div>
      )}
    </div>
  );
};

const ActionRoute: React.FC = () => {
  const { actionId } = useParams();
  const found = actions.find(a => a.id === actionId);
  if (!found) {
    return <div className="text-sm" style={{ color: 'var(--text-dim)' }}>Action not found.</div>;
  }
  
  // Load WildSearch component for search action
  if (actionId === 'search') {
    const WildSearchComp = loadFeatureComponent('WildSearch');
    if (WildSearchComp) {
      return (
        <div className="h-full flex flex-col">
          <h2 className="text-xl font-semibold mb-2 shrink-0">{found.label}</h2>
          <div className="flex-1 overflow-hidden">
            <React.Suspense fallback={<div className="p-4 text-sm" style={{ color: 'var(--text-dim)' }}>Loading...</div>}>
              <WildSearchComp />
            </React.Suspense>
          </div>
        </div>
      );
    }
  }
  
  return (
    <div>
      <h2 className="text-xl font-semibold mb-2">{found.label}</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Showing action: {found.id}</p>
      <div className="text-sm" style={{ color: 'var(--text-dim)' }}>No module implemented yet for action "{found.id}".</div>
    </div>
  );
};

// AppInner definition moved here
const AppInner: React.FC = () => {
  const { 
    currentUser, 
    isAdmin, 
    toolboxRole,
    userTenant,
    signOut: authSignOut, 
    loading,
    loginRole,
    updateLoginRole
  } = useAuth();

  const navigate = useNavigate();

  // ALL STATE AND HOOKS MUST BE DEFINED BEFORE ANY CONDITIONAL RETURNS
  // State for dynamic menu data
  const [menuData, setMenuData] = useState<MenuGroup[]>([]);
  const [menuLoading, setMenuLoading] = useState(true);
  
  // Other component state
  const [active, setActive] = useState<ActiveFeature | null>(null);
  const [openGroup, setOpenGroup] = useState<string>('');
  const [showMenu, setShowMenu] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Theme state
  type Theme = 'light' | 'dark' | 'vscode-dark' | 'vscode-light' | 'one-dark-pro';
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem('theme') : null;
    if (stored === 'dark' || stored === 'light' || stored === 'vscode-dark' || stored === 'vscode-light' || stored === 'one-dark-pro') return stored as Theme;
    const prefersDark = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  });
  
  // Refs
  const logoRef = useRef<HTMLDivElement | null>(null);
  const groupRefs = useRef<HTMLButtonElement[]>([]);
  const featureRefs = useRef<HTMLButtonElement[]>([]);
  
  // Focus tracking state
  const [groupFocusIndex, setGroupFocusIndex] = useState<number>(-1);
  const [featureFocusIndex, setFeatureFocusIndex] = useState<number>(-1);
  
  // Status bar enhanced content  
  const [statusContent, setStatusContent] = useState<React.ReactNode | null>(null);
  const [statusExpanded, setStatusExpanded] = useState(false);
  const statusScrollRef = useRef<HTMLDivElement | null>(null);

  // ALL USEEFFECTS MUST BE DEFINED HERE BEFORE CONDITIONAL RETURNS
  
  // Update openGroup when menuData loads
  useEffect(() => {
    if (menuData.length > 0 && !openGroup) {
      setOpenGroup(menuData[0].id);
    }
  }, [menuData, openGroup]);
  
  // Reset feature refs when group changes
  useEffect(() => { 
    featureRefs.current = []; 
    setFeatureFocusIndex(0); 
  }, [openGroup]);
  
  // When menu re-opens, restore focus to current group for keyboard users
  useEffect(() => {
    if (showMenu) {
      const idx = menuData.findIndex(g => g.id === openGroup) || 0;
      setGroupFocusIndex(idx);
      setTimeout(() => groupRefs.current[idx]?.focus(), 0);
    }
  }, [showMenu, openGroup, menuData]);

  // Load feature data and set up role-based menu when user logs in
  useEffect(() => {
    const loadMenuData = async () => {
      try {
        if (currentUser && loginRole) {
          // Load all feature data
          const allFeatureData = await loadFeatureData();
          
          // Get accessible menu groups based on user's role
          const accessibleGroups = getAccessibleMenuGroups(allFeatureData, loginRole);
          
          setMenuData(accessibleGroups);
        } else {
          // Initialize with basic data if no role yet
          await initializeMenuData();
          setMenuData([]);
        }
      } catch (error) {
        console.error('Error loading menu data:', error);
        logError('Failed to load menu data', error instanceof Error ? { message: error.message } : {});
        // Fallback to initialize basic menu
        await initializeMenuData();
        setMenuData([]);
      } finally {
        setMenuLoading(false);
      }
    };

    loadMenuData();
  }, [currentUser, loginRole]);

  // Update login role when user signs in
  useEffect(() => {
    const updateUserRole = async () => {
      if (currentUser && !loginRole) {
        try {
          const { tenantId, uid } = await getUserTenantAndUid(currentUser);
          if (tenantId && uid) {
            const result = await fetchUserCustomClaims(tenantId, uid, currentUser);
            const role = result?.toolboxRole || 'netzoom'; // Default to 'netzoom' role for testing
            updateLoginRole(role);
          } else {
            updateLoginRole('netzoom'); // Default if no tenant info
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
          updateLoginRole('netzoom'); // Fallback to default role
        }
      }
    };

    updateUserRole();
  }, [currentUser, loginRole, updateLoginRole]);

  // Theme effect
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark'); else root.classList.remove('dark');
    root.dataset.theme = theme.includes('vscode') || theme === 'one-dark-pro' ? theme : '';
    try { localStorage.setItem('theme', theme); } catch { 
      // Ignore localStorage errors in incognito mode
    }
  }, [theme]);

  // Status bar scroll effect
  useEffect(() => {
    if (statusExpanded && statusScrollRef.current) {
      const el = statusScrollRef.current;
      el.scrollTop = el.scrollHeight;
    }
  }, [statusContent, statusExpanded]);

  // ALL CALLBACKS AND FUNCTIONS MUST BE DEFINED HERE BEFORE CONDITIONAL RETURNS
  const collapseStatus = useCallback(() => {
    setStatusExpanded(false);
    setStatusContent(null);
  }, []);

  const invokeFeature = useCallback((item: FeatureItem) => {
    if (item.disabled) return; // Don't invoke disabled features
    try {
      navigate(`/feature/${item.id}`);
      setActive({ type: 'feature', id: item.id, label: item.label });
      setError(null);
      setShowMenu(false);
    } catch (e) {
      logError(e, { where: 'invokeFeature', feature: item.id });
      setError((e as Error).message || 'Unknown error');
    }
  }, [navigate]);

  const invokeAction = useCallback(async (id: ActionId) => {
    try {
      if (id === 'theme') {
        setTheme(t => {
          if (t === 'light') return 'dark';
          if (t === 'dark') return 'vscode-dark';
          if (t === 'vscode-dark') return 'one-dark-pro';
          if (t === 'one-dark-pro') return 'vscode-light';
          return 'light';
        });
        setActive({ type: 'action', id: 'theme', label: 'Theme toggled' });
        return;
      }
      if (id === 'logout') {
        const confirmed = window.confirm('Are you sure you want to sign out?');
        if (confirmed) {
          try {
            console.log('Signing out user...');
            await authSignOut();
            setActive({ type: 'action', id: 'logout', label: 'Signed out successfully' });
            // Page will automatically redirect to login after auth state changes
          } catch (error) {
            logError(error, { where: 'logout' });
            setError('Sign out failed. Please try again.');
            console.error('Logout error:', error);
          }
        }
        return;
      }
      if (id === 'console') {
        const now = new Date().toLocaleTimeString();
        setStatusContent(
          <div className="space-y-1 text-[11px] leading-relaxed font-mono">
            <div>[{now}] Console opened.</div>
            <div>[{now}] Loading recent activity…</div>
            <div>[{now}] Metrics: latency=123ms throughput=456rps</div>
            <div>[{now}] Build: a1b2c3 commit synced.</div>
            <div>[{now}] Tip: Press ESC to hide menu.</div>
          </div>
        );
        setStatusExpanded(true);
        setActive({ type: 'action', id: 'console', label: 'Console opened' });
        return;
      }
      const found = actions.find(a => a.id === id)!;
      if (found.id === 'help' || found.id === 'search' || found.id === 'theme') {
        navigate(`/action/${found.id}`);
      }
      setActive({ type: 'action', id: found.id, label: found.label });
      setError(null);
    } catch (e) {
      logError(e, { where: 'invokeAction', action: id });
      setError('Action failed');
    }
  }, [navigate, authSignOut]);

  const focusGroupByIndex = useCallback((idx: number) => {
    const clamped = Math.max(0, Math.min(menuData.length - 1, idx));
    setGroupFocusIndex(clamped);
    setTimeout(() => {
      groupRefs.current[clamped]?.focus();
      setOpenGroup(menuData[clamped].id);
    }, 0);
  }, [menuData]);

  const focusFeatureByIndex = useCallback((idx: number) => {
    const list = featureRefs.current;
    if (!list.length) return;
    const count = list.length;
    const mod = ((idx % count) + count) % count; // wrap
    const target = list[mod];
    if (target) {
      setFeatureFocusIndex(mod);
      target.focus();
    }
  }, []);

  const onGroupKeyDown = useCallback((e: React.KeyboardEvent, index: number) => {
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); focusGroupByIndex(index + 1); break;
      case 'ArrowUp': e.preventDefault(); focusGroupByIndex(index - 1); break;
      case 'Home': e.preventDefault(); focusGroupByIndex(0); break;
      case 'End': e.preventDefault(); focusGroupByIndex(menuData.length - 1); break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        setOpenGroup(menuData[index].id);
        break;
    }
  }, [menuData, focusGroupByIndex]);

  const onFeatureKeyDown = useCallback((e: React.KeyboardEvent, fi: number, group: MenuGroup, f: FeatureItem) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusFeatureByIndex(fi + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusFeatureByIndex(fi - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        invokeFeature(f);
        break;
      case 'Escape': {
        e.preventDefault();
        const gIdx = menuData.findIndex(g => g.id === group.id);
        if (gIdx !== -1) {
          setGroupFocusIndex(gIdx);
          setTimeout(() => groupRefs.current[gIdx]?.focus(), 0);
        }
        break;
      }
    }
  }, [menuData, invokeFeature, focusFeatureByIndex]);
  
  // Show loading state while loading menu
  if (loading || menuLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Note: User authentication is now handled at AppRoot level
  // This component only renders for authenticated users
  
  // Show access denied if not admin or netzoom user
  // Match CloudRun API logic: accept admin=true OR toolboxRole="netzoom" OR toolboxRole="admin"
  const hasAccess = isAdmin || toolboxRole === 'netzoom' || toolboxRole === 'admin';
  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="mt-6 text-2xl font-bold text-gray-900">Access Denied</h2>
          <p className="mt-2 text-gray-600">
            You don't have the required privileges to access this application. Please contact an administrator.
          </p>
          <p className="mt-4 text-sm text-gray-500">
            Signed in as: {currentUser?.email || currentUser?.uid || 'Unknown'}
          </p>
          <p className="mt-2 text-xs text-gray-400">
            UID: {currentUser?.uid}
          </p>
          <div className="mt-6 space-y-3">
            <div className="flex gap-3 justify-center">
              <button
                onClick={async () => {
                  try {
                    console.log('Signing out...');
                    await authSignOut();
                    window.location.reload();
                  } catch (error) {
                    console.error('Logout error:', error);
                  }
                }}
                className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
                Sign Out
              </button>
              <button
                onClick={async () => {
                  console.log('Attempting to refresh token...');
                  try {
                    await currentUser?.getIdToken(true);
                    const decodedToken = await currentUser?.getIdTokenResult(true);
                    console.log('Token refreshed successfully');
                    if (decodedToken) {
                      console.log('Current token claims:', decodedToken.claims);
                    }
                    window.location.reload();
                  } catch (error) {
                    console.error('Token refresh failed:', error);
                  }
                }}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Refresh Permissions
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Sign out to try with different credentials, or refresh to check for updated permissions.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <header className="flex items-center px-4 gap-4 h-14 border-b" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border)' }}>
        <div
          ref={logoRef}
          className="flex items-center gap-2 font-semibold select-none outline-none"
          role="banner"
          style={{ 
            position: 'relative',
            flexShrink: 0,
            marginLeft: 0,
            marginRight: 'auto',
            justifyContent: 'flex-start'
          }}
        >
          <div
            className="flex items-center"
            onMouseEnter={() => setShowMenu(true)}
            onFocus={() => setShowMenu(true)}
          >
            <button
              type="button"
              onClick={() => setShowMenu(v => !v)}
              className="p-1 rounded-md hover:bg-opacity-10 hover:bg-white focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-white transition-colors"
              title="Toggle menu"
              aria-label="Toggle menu"
              style={{ color: 'var(--accent)' }}
            >
              <Bars3Icon className="h-5 w-5" />
            </button>
          </div>
          <div className="w-36 h-16 rounded overflow-hidden flex items-center justify-center" style={{ padding: 2 }}>
            <img
              src="/NetZoom_Logo_Red.svg"
              alt="NetZoom Logo"
              className="h-full w-full object-contain"
              style={{ display: 'block' }}
            />
          </div>
          <span className="hidden sm:inline text-lg tracking-wide">
            {logoText}
          </span>
        </div>
        
        {/* User Info Display */}
        <div className="flex items-center gap-3 ml-auto mr-4">
          {currentUser && (
            <div className="flex items-center gap-2 text-sm">
              <div className="text-right">
                <div className="font-medium" style={{ color: 'var(--text)' }}>
                  {currentUser.email}
                </div>
                <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                  {(loginRole || toolboxRole) && (
                    <div className="relative">
                      <select
                        value={loginRole || toolboxRole || 'vss'}
                        onChange={(e) => updateLoginRole(e.target.value as 'admin' | 'vss' | 'netzoom')}
                        className={`px-2 py-0.5 rounded-full text-xs font-medium border-0 outline-none cursor-pointer ${
                          (loginRole || toolboxRole) === 'admin' ? 'bg-red-100 text-red-800' :
                          (loginRole || toolboxRole) === 'vss' ? 'bg-blue-100 text-blue-800' :
                          (loginRole || toolboxRole) === 'netzoom' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}
                        title="Switch Role"
                      >
                        <option value="admin" className="text-black">Admin</option>
                        <option value="vss" className="text-black">VSS</option>
                        <option value="netzoom" className="text-black">NetZoom</option>
                      </select>
                    </div>
                  )}
                  {userTenant && userTenant !== 'main-auth' && (
                    <span className="text-xs opacity-70">
                      {userTenant}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          {actions.map(action => (
            <button
              key={action.id}
              onClick={() => invokeAction(action.id)}
              className="p-2 rounded-md hover:bg-opacity-10 hover:bg-white focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-white transition-colors"
              title={action.label}
              style={{ color: 'var(--accent)' }}
            >
              {action.icon}
            </button>
          ))}
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar Menu */}
        <div 
          className="border-r flex flex-col overflow-hidden transition-all duration-300 ease-in-out" 
          style={{ 
            background: 'var(--bg-surface)', 
            borderColor: 'var(--border)',
            width: showMenu ? '256px' : '0px',
            minWidth: showMenu ? '256px' : '0px',
            opacity: showMenu ? 1 : 0
          }}
          onMouseLeave={() => setShowMenu(false)}
        >
          <div className="flex-1 overflow-y-auto p-4" style={{ width: '256px' }}>
            {menuData.map((group, index) => {
                const isActiveGroup = group.id === openGroup;
                return (
                  <div key={group.id} className="mb-4">
                    <button
                      ref={el => { if (el) groupRefs.current[index] = el; }}
                      onClick={() => {
                        setOpenGroup(group.id);
                        // Auto-select first available (non-disabled) feature when menu is clicked
                        const firstAvailableFeature = group.features.find(f => !f.disabled);
                        if (firstAvailableFeature) {
                          invokeFeature(firstAvailableFeature);
                        }
                      }}
                      className="w-full flex items-center gap-2 px-2 py-2 rounded text-left text-sm font-medium focus:outline-none transition"
                      style={{ 
                        background: isActiveGroup ? 'var(--bg-accent)' : 'transparent',
                        color: 'var(--text)' 
                      }}
                      onMouseEnter={(e) => {
                        if (!isActiveGroup) {
                          e.currentTarget.style.background = 'var(--hover-bg, var(--bg-accent))';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActiveGroup) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                      aria-expanded={isActiveGroup}
                      aria-controls={`features-${group.id}`}
                      onKeyDown={(e) => onGroupKeyDown(e, index)}
                      tabIndex={index === groupFocusIndex ? 0 : -1}
                    >
                      <span style={{ color: 'var(--accent)' }}>{group.icon}</span>
                      <span className="truncate">{group.label}</span>
                    </button>
                    {isActiveGroup && (
                      <div id={`features-${group.id}`} className="mt-1 ml-4 border-l pl-2 space-y-1" style={{ borderColor: 'var(--border)' }} role="menu" aria-label="Features">
                        {group.features.map((f, fi) => (
                          <button
                            key={f.id}
                            ref={el => { if (el) featureRefs.current[fi] = el; }}
                            onClick={() => f.disabled ? null : invokeFeature(f)}
                            disabled={f.disabled}
                            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-xs sm:text-sm focus:outline-none transition ${f.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                            style={{ 
                              background: 'transparent', 
                              color: f.disabled ? 'var(--text-dim, #999)' : 'var(--text)' 
                            }}
                            onMouseEnter={(e) => { 
                              if (!f.disabled) {
                                e.currentTarget.style.background = 'var(--hover-bg, var(--bg-accent))'; 
                              }
                            }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            role="menuitem"
                            onKeyDown={(e) => onFeatureKeyDown(e, fi, group, f)}
                            tabIndex={fi === featureFocusIndex ? 0 : -1}
                            title={f.disabled ? `Feature requires ${f.toolboxRole || 'specific'} role` : (f.tooltip || f.label)}
                          >
                            <span style={{ color: f.disabled ? 'var(--text-dim, #999)' : 'var(--accent)' }}>{f.icon}</span>
                            <span className="truncate">{f.label}</span>
                            {f.disabled && (
                              <span className="ml-auto text-xs opacity-60">[{f.toolboxRole}]</span>
                            )}
                          </button>
                        ))}
                        {group.features.length === 0 && (
                          <div className="text-xs opacity-70 px-1 py-1">No features.</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

        <main className="flex-1 relative overflow-hidden transition-all duration-300 ease-in-out">
          <div className="h-full p-6" style={{ background: 'var(--bg)' }} onMouseEnter={() => !showMenu && setShowMenu(false)}>
            <MenuDataContext.Provider value={menuData}>
              <Routes>
                <Route path="/feature/:featureId" element={<FeatureRoute />} />
                <Route path="/action/:actionId" element={<ActionRoute />} />
                <Route path="/" element={<Navigate to="/feature/dashboard-Dashboard" replace />} />
                <Route path="*" element={<Navigate to="/feature/dashboard-Dashboard" replace />} />
              </Routes>
            </MenuDataContext.Provider>
          </div>
        </main>
      </div>
    </div>
  );
};

// Export AppInner as App (wrapper is now handled by MainApp.tsx)
export const App = AppInner;
